import { CssBaseline, Stack, ThemeProvider, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CommonModal, createAppTheme, Loader, Toast } from './components';
import { ImpersonationAttentionModal } from './components/modals/ImpersonationAttentionModal';
import { NetworkDisconnectionModal } from './components/modals/NetworkDisconnectionModal';
import './components/theme/index.css';
import { ImpersonationTimer } from './components/ui-components/ImpersonationTimer';
import { useContracts } from './hooks/useContracts';
import { useImpersonation } from './hooks/useImpersonation';
import { useLogoutSync } from './hooks/useLogoutSync';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { usePageTracking } from './hooks/usePageTracking';
import { usePendingLinkRedirect } from './hooks/usePendingLinkRedirect';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { useSessionValidation } from './hooks/useSessionValidation';
import AccountSetup from './pages/account-setup';
import { NonAuthenticatedRoutes } from './routes/non-authenticated-routes';
import { PostAuthenticatedRoutes } from './routes/post-authenticated-routes';
import { getBrandDetails } from './services/brand-service';
import { getProfileDetails } from './services/user-service';
import { RootState } from './store';
import { setBrandDetails, setBrandLoading } from './store/reducers/brandSlice';
import { closeOfflineModal } from './store/reducers/networkSlice';
import { resetToast } from './store/reducers/toastSlice';
import { updateUserProfileDetails } from './store/reducers/userSlice';
import { clearTabSessionMarker, consumeJustLoggedInFlag, markTabSessionActive } from './utils/auth-session-cleanup';
import { getBrandFromCache, saveBrandToCache } from './utils/brand-cache';
import { initGA } from './utils/google-analytics';
import { isElloPaymentLinkContext } from './utils/navigation-constants';

const App: React.FC = () => {
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const showToast = useSelector((state: RootState) => state.toast);
    const { showOfflineModal } = useSelector((state: RootState) => state.network);
    const dispatch = useDispatch();
    const { activeHTTPRequests } = useSelector((state: RootState) => state.app);
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const isBrandLoading = useSelector((state: RootState) => state.brand.isBrandLoading);
    const prevLoadingStateRef = React.useRef(true);

    // Create theme dynamically based on brand's primary color and font
    const theme = useMemo(() => {
        const primaryColor = brandDetails?.PrimaryColour || '#001E60';
        const font = brandDetails?.Font;
        return createAppTheme(primaryColor, font);
    }, [brandDetails?.PrimaryColour, brandDetails?.Font]);

    // Network status hook
    useNetworkStatus();

    // Session timeout hook
    const { showWarning, timeRemaining, handleRefreshToken, handleLogout, handleCloseModal, isRefreshing } =
        useSessionTimeout();

    // Session validation hook (runs once on app load to verify stored tokens are still valid)
    useSessionValidation();

    // Cross-tab logout synchronization
    useLogoutSync();

    // Handle redirects after login when there's a pending make-payment or autopay request
    usePendingLinkRedirect();

    useEffect(() => {
        if (isLoggedIn) {
            markTabSessionActive();
        } else {
            clearTabSessionMarker();
        }
    }, [isLoggedIn]);

    // Super user impersonation handler
    const { impersonation } = useImpersonation();

    // While impersonating, userDetails only holds a 'Super User' placeholder. Load the impersonated
    // user's real profile once per session on any page (not only the dashboard) so names/details
    // (header, autopay form, etc.) are correct after a direct load or refresh.
    const profileFetchedForTokenRef = React.useRef<string | null>(null);
    useEffect(() => {
        if (!impersonation.isImpersonating || !impersonation.token) {
            return;
        }
        if (profileFetchedForTokenRef.current === impersonation.token) {
            return;
        }
        profileFetchedForTokenRef.current = impersonation.token;
        getProfileDetails(false)
            .then(response => {
                const profileDetails = response?.data?.data || response?.data;
                if (profileDetails && typeof profileDetails === 'object') {
                    dispatch(updateUserProfileDetails(profileDetails));
                }
            })
            .catch(error => console.error('Error fetching impersonated user details:', error));
    }, [impersonation.isImpersonating, impersonation.token, dispatch]);

    // Show the post-login setup screen right after a fresh login. This is plain conditional
    // rendering gated by local state — not a route/navigate() — because navigating to a
    // dedicated URL raced PostAuthenticatedRoutes' own "/login -> ContractOverview" redirect
    // for already-authenticated users (and lost, unpredictably, across repeated attempts).
    // Local state has no such race: it's read directly in this render, not via the router.
    // Also re-checked on impersonation.showAttentionModal changing: impersonation marks the
    // flag only once the attention modal closes (so the loader isn't hidden behind it), which
    // happens without an isLoggedIn transition since isLoggedIn was already true.
    const [showAccountSetup, setShowAccountSetup] = React.useState(false);
    useEffect(() => {
        if (isLoggedIn && consumeJustLoggedInFlag()) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setShowAccountSetup(true);
        }
    }, [isLoggedIn, impersonation.showAttentionModal]);

    // Contracts are no longer persisted to localStorage (they can go stale across sessions),
    // so fetch them once here whenever the user is logged in — covers app load/refresh on any
    // page, not just the ones that already fetch contracts themselves. useContracts also
    // de-dupes concurrent fetches from other screens and re-fetches on resume after idle.
    const { fetchContracts } = useContracts();
    const contractsFetchedRef = React.useRef(false);
    const lastImpersonationTokenRef = React.useRef<string | null>(null);
    useEffect(() => {
        if (!isLoggedIn) {
            contractsFetchedRef.current = false;
            lastImpersonationTokenRef.current = impersonation.token;
            return;
        }
        // A new impersonation token means the session identity changed even though isLoggedIn
        // stayed true (e.g. an already-authenticated tab opening an impersonation link) — the
        // one-shot guard below must not suppress the prefetch in that case.
        if (impersonation.token !== lastImpersonationTokenRef.current) {
            lastImpersonationTokenRef.current = impersonation.token;
            contractsFetchedRef.current = false;
        }
        if (contractsFetchedRef.current) return;
        contractsFetchedRef.current = true;
        // Errors are already logged/recorded in Redux by useContracts; nothing else to do here.
        fetchContracts(true).catch(() => undefined);
    }, [isLoggedIn, fetchContracts, impersonation.token]);

    const { pathname, search } = useLocation();
    const closeToast = () => {
        dispatch(resetToast({ show: false, message: null, isAlertOpen: false }));
    };

    /* ------------------ Google Analytics ------------------ */
    // Initialize Google Analytics ONCE on app load
    useEffect(() => {
        initGA();
    }, []);

    //Track page views globally (auth + non-auth)
    usePageTracking();

    // Fetch brand details on app load using stale-while-revalidate strategy:
    // 1. brandSlice initialState already hydrated from localStorage cache (zero-flicker on cached loads)
    // 2. This effect runs a background API call to refresh the cache with fresh data
    // 3. setBrandLoading(true) is only dispatched when there is NO cached brand (first-ever load)
    //    — avoids the color flash that previously occurred on every page load / post-login navigation
    useEffect(() => {
        const fetchBrandDetails = async () => {
            const hasCachedBrand = !!getBrandFromCache();

            // Only show loading state on first-ever load (no cache available yet).
            // When cache exists, keep rendering with cached brand — no flicker.
            if (!hasCachedBrand) {
                dispatch(setBrandLoading(true));
            }

            try {
                const response = await getBrandDetails(false);
                if (response.statusCode === 200 && response.data) {
                    // Save to localStorage so next load (and post-login navigation) is flicker-free.
                    saveBrandToCache(response.data);
                    dispatch(setBrandDetails(response.data));
                } else if (!hasCachedBrand) {
                    dispatch(setBrandLoading(false));
                }
            } catch (error: unknown) {
                console.error('Error fetching brand details:', error);
                if (!hasCachedBrand) {
                    dispatch(setBrandLoading(false));
                }
            }
        };
        fetchBrandDetails();
    }, [dispatch]);

    // Update tab icon (favicon) dynamically based on brandDetails.FavLogo.
    // Preloads the image before swapping the href so the animated-spinner favicon
    // (set in index.html) is replaced atomically — no visible fetch delay.
    useEffect(() => {
        const wasLoading = prevLoadingStateRef.current;
        prevLoadingStateRef.current = isBrandLoading;

        if (!wasLoading || isBrandLoading) return; // only act on loading → done transition

        const applyFavicon = (url: string) => {
            const faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement;
            if (faviconLink) faviconLink.href = url;
            const appleTouchIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement;
            if (appleTouchIcon) appleTouchIcon.href = url;
        };

        const favLogoPath = brandDetails?.FavLogo;

        if (!favLogoPath || typeof favLogoPath !== 'string') {
            // No custom favicon — clear the animated spinner with a blank static icon
            applyFavicon("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'/>");
            return;
        }

        let faviconUrl = favLogoPath;
        if (!favLogoPath.startsWith('http://') && !favLogoPath.startsWith('https://')) {
            const profileUrlBase = process.env.REACT_APP_PROFILE_URL || '';
            faviconUrl = `${profileUrlBase}${favLogoPath}`;
        }
        // No cache-buster: allows browser caching and ensures the preloaded image
        // is reused when we set the href (same URL = cache hit = instant swap).

        // Preload the favicon image; only update the link element once it's in memory.
        // This prevents the animated spinner from persisting during the download.
        const img = new Image();
        img.onload = () => applyFavicon(faviconUrl);
        img.onerror = () =>
            applyFavicon("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'/>");
        img.src = faviconUrl;
    }, [isBrandLoading, brandDetails?.FavLogo]);

    // Update browser tab title dynamically based on brandDetails.PortalName
    // Show "Loading..." during brand details loading to prevent flicker
    useEffect(() => {
        if (isBrandLoading) {
            document.title = 'Loading...';
        } else if (brandDetails?.PortalName?.trim()) {
            document.title = brandDetails.PortalName.trim();
        } else {
            document.title = 'Customer Portal'; // Fallback if no portal name is provided
        }
    }, [brandDetails?.PortalName, isBrandLoading]);

    React.useEffect(() => {
        const storedVersion = localStorage.getItem('APP_VERSION');
        if (storedVersion && storedVersion !== process.env.REACT_APP_APP_VERSION) {
            localStorage.setItem('APP_VERSION', process.env.REACT_APP_APP_VERSION ?? '');
            window.location.reload();
        } else if (!storedVersion) {
            localStorage.setItem('APP_VERSION', process.env.REACT_APP_APP_VERSION ?? '');
        }
    }, []);

    const handleCloseOfflineModal = () => {
        dispatch(closeOfflineModal());
    };

    // Get browser location from geolocation
    useEffect(() => {
        async function getAddress(lat: number, lng: number) {
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
            const data = await res.json();
            return data;
        }

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async pos => {
                    localStorage.removeItem('browserLocation');
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;
                    const address = await getAddress(lat, lng);
                    localStorage.setItem('browserLocation', JSON.stringify(address.address.country));
                },
                error => {
                    console.error('Error getting geolocation:', error);
                },
            );
        }
    }, []);

    // Format time remaining text
    const getTimeRemainingText = () => {
        const { seconds } = timeRemaining;
        if (seconds > 0) {
            return `Your session will expire in ${seconds} second${seconds !== 1 ? 's' : ''}. Would you like to remain logged in?`;
        }
        return 'Your session will expire soon. Would you like to remain logged in?';
    };

    // Prevent closing session timeout modal on backdrop click
    const handleCloseSessionModal = (_event: object, reason?: string) => {
        // Only allow closing via explicit button clicks (X button), not backdrop click
        if (reason === 'backdropClick') {
            return;
        }
        // Close modal when X button is clicked
        handleCloseModal();
    };

    // Block ALL rendering until brand config is fetched — prevents any flicker of default/fallback branding.
    // The Loader is position:fixed so it covers the full viewport with no app content behind it.
    if (isBrandLoading) {
        return <Loader />;
    }

    return (
        <Stack className="app" alignItems={'center'}>
            <Stack width="100%" maxWidth={2560}>
                <ThemeProvider theme={theme}>
                    {/* AccountSetup renders its own loader; skip the global one to avoid two stacked loaders */}
                    {activeHTTPRequests >= 1 && !(isLoggedIn && showAccountSetup) && <Loader />}
                    <Toast
                        open={showToast?.show}
                        message={showToast?.message}
                        severity={showToast?.severity as 'error' | 'warning' | 'info' | 'success' | undefined}
                        handleClose={closeToast}
                    />
                    {/* Network Disconnection Modal */}
                    <NetworkDisconnectionModal open={showOfflineModal} onClose={handleCloseOfflineModal} />

                    {/* Impersonation Attention Modal */}
                    <ImpersonationAttentionModal />

                    {/* Impersonation Session Timer */}
                    {impersonation.isImpersonating &&
                        !impersonation.showAttentionModal &&
                        pathname !== '/update_portal' &&
                        !isElloPaymentLinkContext(pathname, search) && (
                            <ImpersonationTimer
                                minutes={impersonation.timeRemaining.minutes}
                                seconds={impersonation.timeRemaining.seconds}
                            />
                        )}

                    {/* Session Timeout Warning Modal - Only show when NOT impersonating */}
                    {!impersonation.isImpersonating && (
                        <CommonModal
                            modalOpen={timeRemaining.minutes === 0 && timeRemaining.seconds === 0 ? false : showWarning}
                            primaryBtnClick={handleLogout}
                            secondaryBtnClick={handleRefreshToken}
                            secondaryBtnDisabled={isRefreshing}
                            onCloseModal={() => handleCloseSessionModal({})}
                            DialogMainTitle="Session Expiring Soon"
                            secondaryBtnName={isRefreshing ? 'Please wait…' : 'Continue'}
                            ModalMaxWidth={350}
                            sx={{
                                '&.MuiModal-root': {
                                    zIndex: 1400,
                                },
                            }}
                            showPrimaryButton={false}
                        >
                            <Typography
                                variant="body1"
                                id="session-timeout-modal-description"
                                color="textSecondary"
                                textAlign="center"
                                sx={{ mb: 1 }}
                            >
                                {getTimeRemainingText()}
                            </Typography>
                        </CommonModal>
                    )}
                    {/* Session Timeout Warning Modal Start */}
                    <CssBaseline />
                    {isLoggedIn && showAccountSetup ? (
                        <AccountSetup onDone={() => setShowAccountSetup(false)} />
                    ) : isLoggedIn ? (
                        <PostAuthenticatedRoutes />
                    ) : (
                        <NonAuthenticatedRoutes />
                    )}
                </ThemeProvider>
            </Stack>
        </Stack>
    );
};

export { App };
